import { useMemo, useState } from 'react';
import { CopyOutlined, LockOutlined } from '@ant-design/icons';

import { Button, Input } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { cotKhoa, type Suggestion } from '../../nghiep-vu/chon-cot';

/**
 * Chọn cột cho MỘT bảng, ngay trong hộp "Chọn bảng từ CSDL" (port `ChonCot.tsx` native).
 *
 * Ở bước khai, không phải sau khi khai: cái giá việc bỏ cột tránh được — dựng cột trong kho bản sao, chép mỗi chu kỳ,
 * nhét tên vào lược đồ gửi mô hình — phát sinh NGAY LÚC bấm Lưu. Đo trên SAP B1: `OINV` 561 cột, một câu hỏi doanh thu
 * cần chừng 15. Mặc định GIỮ HẾT: bỏ bớt là việc có chủ ý, mặc định bỏ theo phép đoán là bản khai thiếu cột mà không ai bấm.
 */
export function ChonCot({
  suggestion,
  bo,
  onDoi,
  onCopy,
}: {
  suggestion: Suggestion;
  /** Tên cột đang BỎ (tập rỗng = chưa ai bỏ gì). */
  bo: Set<string>;
  onDoi: (bo: Set<string>) => void;
  /** Mở hộp chép bộ cột này sang bảng khác. Không truyền thì nút không hiện. */
  onCopy?: () => void;
}) {
  const { t, locale } = useT();
  const [tim, setTim] = useState('');

  const cot = useMemo(() => suggestion.columns ?? [], [suggestion.columns]);
  const khoa = useMemo(() => cotKhoa(suggestion), [suggestion]);

  const loc = useMemo(() => {
    const q = tim.trim().toLowerCase();
    if (!q) return cot;
    return cot.filter((c) => c.name.toLowerCase().includes(q) || (c.label ?? '').toLowerCase().includes(q));
  }, [cot, tim]);

  const dao = (ten: string) => {
    if (khoa.has(ten)) return;
    const moi = new Set(bo);
    if (moi.has(ten)) moi.delete(ten);
    else moi.add(ten);
    onDoi(moi);
  };

  // Hai nút áp lên KẾT QUẢ LỌC — thứ biến ô tìm kiếm thành công cụ tắt hàng loạt.
  const giuLoc = () => {
    const moi = new Set(bo);
    for (const c of loc) moi.delete(c.name);
    onDoi(moi);
  };
  const boLoc = () => {
    const moi = new Set(bo);
    for (const c of loc) if (!khoa.has(c.name)) moi.add(c.name);
    onDoi(moi);
  };

  return (
    <div className="mx-2.5 mb-2.5 rounded-lg border border-default bg-surface p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          size="small"
          value={tim}
          onChange={(e) => setTim(e.target.value)}
          placeholder={t('troLyAiKhoChonCot.tim')}
          aria-label={t('troLyAiKhoChonCot.tim')}
          className="min-w-0 flex-1"
        />
        <Button size="sm" variant="ghost" onClick={giuLoc}>
          {t('troLyAiKhoChonCot.giuHet')}
        </Button>
        <Button size="sm" variant="ghost" onClick={boLoc}>
          {t('troLyAiKhoChonCot.boHet')}
        </Button>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-content-secondary">
          {t('troLyAiKhoChonCot.daGiu', { n: soVN(cot.length - bo.size, locale), m: soVN(cot.length, locale) })}
        </p>
        {/* Nút chép đứng cạnh "đang giữ n/m" vì nó chép đúng cái tập ấy. */}
        {onCopy ? (
          <Button size="sm" variant="ghost" icon={<CopyOutlined />} onClick={onCopy}>
            {t('troLyAiKhoChonCot.copySang')}
          </Button>
        ) : null}
      </div>

      <div className="mt-1.5 max-h-56 overflow-y-auto">
        <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
          {loc.map((c) => {
            const ly = khoa.get(c.name);
            const giu = !bo.has(c.name);
            return (
              <button
                key={c.name}
                type="button"
                aria-pressed={giu}
                disabled={Boolean(ly)}
                title={ly ? t(`troLyAiKhoChonCot.khoa.${ly}`) : c.label || c.name}
                onClick={(e) => {
                  e.stopPropagation();
                  dao(c.name);
                }}
                className={cn(
                  'flex min-w-0 items-center gap-1.5 rounded px-1.5 py-1 text-start text-xs transition-colors',
                  ly
                    ? 'cursor-not-allowed bg-subtle text-content-muted'
                    : giu
                      ? 'bg-primary-50 text-content-primary hover:bg-primary-100'
                      : 'text-content-muted line-through hover:bg-subtle',
                )}
              >
                {ly ? <LockOutlined className="shrink-0 text-2xs" aria-hidden /> : null}
                <span className="truncate font-mono">{c.name}</span>
              </button>
            );
          })}
        </div>
        {loc.length === 0 ? (
          <p className="py-3 text-center text-xs text-content-muted">{t('troLyAiKhoChonCot.khongThay')}</p>
        ) : null}
      </div>
    </div>
  );
}
